import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';

const ACCENT = "#2563EB";
const ACCENT_DARK = "#1D4ED8";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const Logo = ({ onClick }) => (
  <div
    data-testid="auth-logo"
    style={{ marginBottom: 32, cursor: 'pointer', display: "flex", justifyContent: "center" }}
    onClick={onClick}
  >
    <img
      src="/inference-logo.svg"
      alt="Inference"
      style={{ height: 100, width: "auto", display: "block" }}
    />
  </div>
);

const Input = ({ label, type = "text", placeholder, value, onChange, hint, error, autoComplete }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "#9CA3AF", marginBottom: 6, letterSpacing: "0.02em" }}>
      {label}
    </label>
    <input
      type={type} placeholder={placeholder} value={value} onChange={onChange} autoComplete={autoComplete}
      data-testid={`auth-input-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        width: "100%", background: "#0D1117", border: `1px solid ${error ? "#EF4444" : BORDER}`,
        borderRadius: 8, padding: "11px 14px", fontSize: 14, color: TEXT_PRIMARY,
        outline: "none", fontFamily: "'DM Sans', sans-serif", transition: "border-color 0.2s, box-shadow 0.2s",
      }}
      onFocus={e => { e.target.style.borderColor = ACCENT; e.target.style.boxShadow = `0 0 0 3px ${ACCENT}18`; }}
      onBlur={e => { e.target.style.borderColor = error ? "#EF4444" : BORDER; e.target.style.boxShadow = "none"; }}
    />
    {error && <p style={{ fontSize: 12, color: "#EF4444", marginTop: 5 }}>{error}</p>}
    {hint && !error && <p style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 5 }}>{hint}</p>}
  </div>
);

const BtnPrimary = ({ children, onClick, loading, disabled, fullWidth = true, testId }) => (
  <button
    onClick={onClick} disabled={loading || disabled}
    data-testid={testId}
    style={{
      width: fullWidth ? "100%" : "auto",
      background: loading || disabled ? "#1E3A6E" : ACCENT,
      color: loading || disabled ? "#6B9AE0" : "#fff",
      border: "none", borderRadius: 8, padding: "12px 20px",
      fontSize: 14, fontWeight: 600, cursor: loading || disabled ? "not-allowed" : "pointer",
      fontFamily: "'DM Sans', sans-serif", transition: "background 0.2s, transform 0.1s",
      letterSpacing: "-0.01em", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    }}
    onMouseEnter={e => { if (!loading && !disabled) e.target.style.background = ACCENT_DARK; }}
    onMouseLeave={e => { if (!loading && !disabled) e.target.style.background = ACCENT; }}
  >
    {loading ? (
      <>
        <span style={{ width: 14, height: 14, border: "2px solid #6B9AE0", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
        Processing…
      </>
    ) : children}
  </button>
);

const Divider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "22px 0" }}>
    <div style={{ flex: 1, height: 1, background: BORDER }} />
    <span style={{ fontSize: 12, color: TEXT_DIM, letterSpacing: "0.04em" }}>{label}</span>
    <div style={{ flex: 1, height: 1, background: BORDER }} />
  </div>
);

const GoogleBtn = ({ label, onClick }) => (
  <button onClick={onClick} data-testid="google-login-btn" style={{
    width: "100%", background: SURFACE, border: `1px solid ${BORDER}`,
    borderRadius: 8, padding: "11px 20px", fontSize: 14, color: "#D1D5DB",
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    transition: "border-color 0.2s, background 0.2s",
  }}
    onMouseEnter={e => e.currentTarget.style.borderColor = "#374151"}
    onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}
  >
    <svg width="17" height="17" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
    {label}
  </button>
);

/**
 * "Continue with Microsoft" button. Only renders if
 * `REACT_APP_MICROSOFT_CLIENT_ID` is set — keeps the button invisible in
 * environments where Microsoft SSO isn't configured, so users never see a
 * broken OAuth handoff.
 *
 * Clicking redirects to the backend `/api/auth/microsoft` endpoint, which
 * initiates the MSAL auth code flow and bounces the browser to
 * login.microsoftonline.com.
 */
const MicrosoftBtn = ({ label, inviteCode }) => {
  if (!process.env.REACT_APP_MICROSOFT_CLIENT_ID) return null;
  const onClick = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const qs = inviteCode ? `?invite_code=${encodeURIComponent(inviteCode)}` : "";
    window.location.href = `${base}/api/auth/microsoft${qs}`;
  };
  return (
    <button onClick={onClick} data-testid="microsoft-login-btn" style={{
      width: "100%", background: "#FFFFFF", border: `1px solid #D1D5DB`,
      borderRadius: 8, padding: "11px 20px", fontSize: 14, color: "#1F2937",
      cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
      transition: "border-color 0.2s, background 0.2s",
      marginTop: 10,
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "#9CA3AF"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "#D1D5DB"}
    >
      {/* Official 4-tile Microsoft mark. Tiles are 9.5×9.5 with a 0.5-unit
          gutter — matches the brand guidelines exact ratio (sans the ™). */}
      <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
      {label}
    </button>
  );
};

/* ── Data Preview Panel (right side) ── */
const DataPanel = () => {
  const signals = [
    { label: "Payments Stack", value: "Multi-provider", conf: 94, color: "#22C55E" },
    { label: "BNPL Detected", value: "Active at checkout", conf: 88, color: "#22C55E" },
    { label: "Checkout Type", value: "Embedded", conf: 91, color: "#22C55E" },
    { label: "Fit Score", value: "87 / 100", conf: 87, color: "#22C55E" },
    { label: "Traffic Trend", value: "+12% MoM", conf: 72, color: "#F59E0B" },
    { label: "Decision Complexity", value: "Medium", conf: 65, color: "#F59E0B" },
  ];

  return (
    <div style={{
      flex: 1, background: "#090B12", borderLeft: `1px solid ${BORDER}`,
      display: "flex", flexDirection: "column", justifyContent: "center",
      padding: "60px 56px", position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(${ACCENT}06 1px, transparent 1px), linear-gradient(90deg, ${ACCENT}06 1px, transparent 1px)`,
        backgroundSize: "40px 40px",
      }} />
      <div style={{
        position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%)",
        width: 400, height: 300,
        background: `radial-gradient(ellipse, ${ACCENT}14 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />

      <div style={{ position: "relative" }}>
        <div style={{ background: "#0D1117", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22C55E", boxShadow: "0 0 6px #22C55E" }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>Example Corp.</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {["eCommerce", "Platform", "Canada"].map(t => (
                  <span key={t} style={{ fontSize: 10, color: TEXT_MUTED, background: "#161B27", border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 7px", fontFamily: "'DM Mono', monospace" }}>{t}</span>
                ))}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: "#22C55E", fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>87</div>
              <div style={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "'DM Mono', monospace", marginTop: 2 }}>FIT SCORE</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {signals.map((s, i) => (
              <div key={i} style={{ background: "#0A0D13", borderRadius: 7, padding: "9px 11px", border: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 3, letterSpacing: "0.05em" }}>{s.label}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 5, fontFamily: "'DM Sans', sans-serif" }}>{s.value}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ flex: 1, height: 2, background: BORDER, borderRadius: 2 }}>
                    <div style={{ width: `${s.conf}%`, height: "100%", background: s.color, borderRadius: 2, opacity: 0.8 }} />
                  </div>
                  <span style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{s.conf}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p style={{ fontSize: 11, color: TEXT_DIM, textAlign: "center", marginTop: 20, fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}>
          LIVE ANALYSIS PREVIEW · SHOPIFY INC.
        </p>
      </div>
    </div>
  );
};

/* ── LOGIN SCREEN ── */
const LoginScreen = ({ onNavigate, onLogin, onGoogleLogin, error: authError }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      await onLogin(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && email && password) handleLogin();
  };

  return (
    <div style={{ flex: 1, width: "100%", padding: "48px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 316 }}>
      <Logo onClick={() => onNavigate("home")} />
      <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 6, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
        Sign in
      </h1>
      <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 32, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
        No account?{" "}
        <span onClick={() => onNavigate("signup")} style={{ color: ACCENT, cursor: "pointer", fontWeight: 500 }} data-testid="switch-to-signup">
          Start free →
        </span>
      </p>

      <GoogleBtn label="Continue with Google" onClick={onGoogleLogin} />
      <MicrosoftBtn label="Continue with Microsoft" />
      <Divider label="or sign in with email" />

      {(error || authError) && (
        <div role="alert" data-testid="login-error" style={{ background: "#450A0A", border: "1px solid #7F1D1D", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#EF4444" }}>
          {error || authError}
        </div>
      )}

      <div onKeyDown={handleKeyDown}>
        <Input label="Work email" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
        <div style={{ marginBottom: 8, position: "relative" }}>
          <Input label="Password" type={showPass ? "text" : "password"} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
          <button onClick={() => setShowPass(s => !s)} style={{ position: "absolute", right: 12, top: 32, background: "none", border: "none", cursor: "pointer", color: TEXT_MUTED, fontSize: 12, fontFamily: "'DM Sans', sans-serif", padding: 0 }}>
            {showPass ? "Hide" : "Show"}
          </button>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -8, marginBottom: 16 }}>
          <span
            data-testid="forgot-password-link"
            onClick={() => onNavigate("forgot")}
            style={{ fontSize: 12, color: ACCENT, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
          >Forgot password?</span>
        </div>
      </div>

      <BtnPrimary onClick={handleLogin} loading={loading} disabled={!email || !password} testId="login-submit-btn">
        Sign in
      </BtnPrimary>

      <p style={{ fontSize: 11, color: TEXT_DIM, textAlign: "center", marginTop: 28, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
        By signing in you agree to our <span style={{ color: TEXT_MUTED, cursor: "pointer" }}>Terms</span> and <span style={{ color: TEXT_MUTED, cursor: "pointer" }}>Privacy Policy</span>.
      </p>
      </div>
    </div>
  );
};

/* ── FORGOT PASSWORD SCREEN ── */
const ForgotPasswordScreen = ({ onNavigate }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      await authAPI.forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      setError("Could not send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ flex: 1, width: "100%", padding: "48px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 316 }}>
        <Logo onClick={() => onNavigate("home")} />
        <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>
          Reset your password
        </h1>
        <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 32, fontFamily: "'DM Sans', sans-serif" }}>
          Enter your work email and we'll send you a link to choose a new password.
        </p>

        {submitted ? (
          <div data-testid="forgot-password-confirmation" style={{
            background: "#022E0E", border: "1px solid #14532D", borderRadius: 8,
            padding: "14px 16px", marginBottom: 20,
            fontSize: 13, color: "#86EFAC", lineHeight: 1.6,
          }}>
            If an account exists for <strong style={{ color: "#BBF7D0" }}>{email}</strong>, you'll receive a reset link shortly. Check your inbox (and spam folder).
          </div>
        ) : (
          <>
            {error && (
              <div role="alert" data-testid="forgot-password-error" style={{ background: "#450A0A", border: "1px solid #7F1D1D", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#EF4444" }}>
                {error}
              </div>
            )}
            <div onKeyDown={(e) => { if (e.key === "Enter" && email) handleSubmit(); }}>
              <Input label="Work email" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
            </div>
            <BtnPrimary onClick={handleSubmit} loading={loading} disabled={!email} testId="forgot-password-submit-btn">
              Send Reset Link
            </BtnPrimary>
          </>
        )}

        <p style={{ fontSize: 12, color: TEXT_MUTED, textAlign: "center", marginTop: 20, fontFamily: "'DM Sans', sans-serif" }}>
          <span data-testid="back-to-login-link" onClick={() => onNavigate("login")} style={{ color: ACCENT, cursor: "pointer", fontWeight: 500 }}>
            ← Back to sign in
          </span>
        </p>
      </div>
    </div>
  );
};

/* ── SIGNUP SCREEN ── */
const SignupScreen = ({ onNavigate, onSignup, onGoogleLogin }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "", inviteCode: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Invite-code field state: idle | checking | valid | invalid
  const [inviteStatus, setInviteStatus] = useState("idle");
  const [inviteError, setInviteError] = useState("");

  // If the user landed here from an SSO callback that rejected them for
  // missing an invite code, surface a clear banner above the form so they
  // know what to do next.
  useEffect(() => {
    try {
      const qp = new URLSearchParams(window.location.search);
      if (qp.get("sso_error") === "invite_required") {
        const provider = qp.get("provider") || "your SSO provider";
        setError(`An invite code is required. Enter your code below, then click "Continue with ${provider === "google" ? "Google" : provider === "microsoft" ? "Microsoft" : provider}" again.`);
      }
    } catch (e) { /* noop */ }
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const setInviteCode = (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, inviteCode: v }));
    // Reset validation state as soon as the user edits the field.
    if (inviteStatus !== "idle") {
      setInviteStatus("idle");
      setInviteError("");
    }
  };

  const validateInviteCode = async () => {
    const code = (form.inviteCode || "").trim();
    if (!code) {
      // Empty on blur is not an error yet — the submit handler enforces required.
      setInviteStatus("idle");
      setInviteError("");
      return;
    }
    setInviteStatus("checking");
    setInviteError("");
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/auth/validate-invite-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data && data.valid) {
        setInviteStatus("valid");
        setInviteError("");
      } else {
        setInviteStatus("invalid");
        setInviteError(data?.message || "Invalid or expired invite code");
      }
    } catch {
      setInviteStatus("invalid");
      setInviteError("Could not validate code — please try again");
    }
  };

  const strength = (() => {
    const p = form.password;
    if (!p) return null;
    if (p.length < 6) return { label: "Weak", color: "#EF4444", width: "25%" };
    if (p.length < 10 || !/[A-Z]/.test(p)) return { label: "Fair", color: "#F59E0B", width: "55%" };
    return { label: "Strong", color: "#22C55E", width: "100%" };
  })();

  const handleSignup = async () => {
    setLoading(true);
    setError("");
    if (!(form.inviteCode || "").trim()) {
      setInviteStatus("invalid");
      setInviteError("Invite code is required");
      setLoading(false);
      return;
    }
    try {
      await onSignup(form.email, form.password, form.name, form.inviteCode.trim());
    } catch (err) {
      const detail = err.response?.data?.detail || "Signup failed";
      if (/invite code/i.test(detail)) {
        setInviteStatus("invalid");
        setInviteError(detail);
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ flex: 1, width: "100%", padding: "48px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 316 }}>
      <Logo onClick={() => onNavigate("home")} />
      <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 6, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
        Create your workspace
      </h1>
      <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 32, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
        Already have an account?{" "}
        <a
          href="/login"
          onClick={(e) => { e.preventDefault(); onNavigate("login"); }}
          style={{ color: ACCENT, cursor: "pointer", fontWeight: 500, textDecoration: "none" }}
          data-testid="switch-to-login"
        >
          Sign in →
        </a>
      </p>

      <GoogleBtn
        label="Continue with Google"
        onClick={() => {
          // Mirror the email-signup flow: require an invite code up front so
          // we don't burn an OAuth round-trip just to come back with the
          // sso_error=invite_required banner.
          const code = (form.inviteCode || "").trim();
          if (!code) {
            setInviteStatus("invalid");
            setInviteError("Enter your invite code before continuing with Google");
            return;
          }
          if (inviteStatus === "invalid") {
            return;
          }
          onGoogleLogin(code);
        }}
      />
      <MicrosoftBtn label="Continue with Microsoft" inviteCode={form.inviteCode} />
      <Divider label="or create with email" />

      {error && (
        <div style={{ background: "#450A0A", border: "1px solid #7F1D1D", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#EF4444" }}>
          {error}
        </div>
      )}

      <Input label="Full name" type="text" placeholder="Alex Johnson" value={form.name} onChange={set("name")} autoComplete="name" />
      <Input label="Work email" type="email" placeholder="you@company.com" value={form.email} onChange={set("email")} autoComplete="email" />
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "#9CA3AF", marginBottom: 6, letterSpacing: "0.02em" }}>
          Invite Code
        </label>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            placeholder="Enter your invite code"
            value={form.inviteCode}
            onChange={setInviteCode}
            onBlur={validateInviteCode}
            data-testid="auth-input-invite-code"
            autoComplete="off"
            style={{
              width: "100%",
              background: "#0D1117",
              border: `1px solid ${
                inviteStatus === "invalid" ? "#EF4444" :
                inviteStatus === "valid"   ? "#22C55E" : BORDER
              }`,
              borderRadius: 8,
              padding: "11px 38px 11px 14px",
              fontSize: 14,
              color: TEXT_PRIMARY,
              outline: "none",
              fontFamily: "'DM Sans', sans-serif",
              transition: "border-color 0.2s, box-shadow 0.2s",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
            }}
            onFocus={e => { e.target.style.borderColor = ACCENT; e.target.style.boxShadow = `0 0 0 3px ${ACCENT}18`; }}
          />
          {inviteStatus === "checking" && (
            <span data-testid="invite-status-checking" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, border: `2px solid ${BORDER}`, borderTopColor: ACCENT, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          )}
          {inviteStatus === "valid" && (
            <svg data-testid="invite-status-valid" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
          {inviteStatus === "invalid" && (
            <svg data-testid="invite-status-invalid" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          )}
        </div>
        {inviteStatus === "invalid" && inviteError && (
          <p data-testid="invite-error" style={{ fontSize: 12, color: "#EF4444", marginTop: 5 }}>{inviteError}</p>
        )}
        {inviteStatus !== "invalid" && (
          <p style={{ fontSize: 11.5, color: TEXT_MUTED, marginTop: 6, fontFamily: "'DM Sans', sans-serif" }}>
            Don't have an invite code? Request access at{" "}
            <a href="https://inferenceapp.io" target="_blank" rel="noreferrer" style={{ color: ACCENT, textDecoration: "none" }}>inferenceapp.io</a>
          </p>
        )}
      </div>
      <div style={{ marginBottom: 18 }}>
        <Input label="Password" type="password" placeholder="Min. 8 characters" value={form.password} onChange={set("password")} autoComplete="new-password" />
        {strength && (
          <div style={{ marginTop: -10 }}>
            <div style={{ height: 3, background: BORDER, borderRadius: 2, overflow: "hidden" }}>
              <div style={{ width: strength.width, height: "100%", background: strength.color, transition: "width 0.3s, background 0.3s" }} />
            </div>
            <span style={{ fontSize: 11, color: strength.color, fontFamily: "'DM Mono', monospace", marginTop: 4, display: "block" }}>{strength.label}</span>
          </div>
        )}
      </div>

      <BtnPrimary onClick={handleSignup} loading={loading} disabled={!form.name || !form.email || !form.password} testId="signup-submit-btn">
        Create Account
      </BtnPrimary>

      <p style={{ fontSize: 12, color: TEXT_MUTED, textAlign: "center", marginTop: 16, fontFamily: "'DM Sans', sans-serif" }}>
        Already have an account?{" "}
        <a
          href="/login"
          data-testid="signup-goto-login"
          onClick={(e) => { e.preventDefault(); onNavigate("login"); }}
          style={{ color: ACCENT, cursor: "pointer", fontWeight: 500, textDecoration: "none" }}
        >
          Sign in
        </a>
      </p>

      <p style={{ fontSize: 11, color: TEXT_DIM, textAlign: "center", marginTop: 24, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
        By creating an account you agree to our <span style={{ color: TEXT_MUTED, cursor: "pointer" }}>Terms</span> and <span style={{ color: TEXT_MUTED, cursor: "pointer" }}>Privacy Policy</span>.
      </p>
      </div>
    </div>
  );
};

/* ── ROOT AUTH PAGE ── */
export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup, loginWithGoogle } = useAuth();
  const [authError, setAuthError] = useState("");

  const initialScreen = location.pathname === '/signup' ? 'signup' : 'login';
  const [screen, setScreen] = useState(initialScreen);

  useEffect(() => {
    setScreen(location.pathname === '/signup' ? 'signup' : 'login');
  }, [location.pathname]);

  const handleNavigate = (target) => {
    if (target === 'home') {
      navigate('/');
    } else if (target === 'signup') {
      navigate('/signup');
    } else if (target === 'login') {
      navigate('/login');
      setScreen('login');
    } else if (target === 'forgot') {
      setScreen('forgot');
    }
  };

  // Resolve where to land after a successful auth: honour any deep-link
  // path stashed by ProtectedRoute (e.g. /app/analysis/<id>) and clear it,
  // otherwise fall back to /app.
  const resolvePostAuthTarget = () => {
    try {
      const stashed = sessionStorage.getItem("inference_post_login_redirect");
      if (stashed && stashed.startsWith("/app/")) {
        sessionStorage.removeItem("inference_post_login_redirect");
        return stashed;
      }
    } catch (e) { /* ignore (private mode, etc.) */ }
    return "/app";
  };

  // After auth, route to the resolved target. For deep-links we use a
  // browser-level navigation (window.location.assign) instead of
  // react-router's `navigate` because PublicRoute's `<Navigate to="/app">`
  // fires in the same render cycle as setUser and races/overwrites the
  // soft navigation, dropping the deep-link path.
  const goPostAuth = () => {
    const target = resolvePostAuthTarget();
    if (target !== "/app") {
      window.location.assign(target);
    } else {
      navigate(target);
    }
  };

  const handleLogin = async (email, password) => {
    await login(email, password);
    goPostAuth();
  };

  const handleSignup = async (email, password, name, inviteCode) => {
    await signup(email, password, name, inviteCode);
    goPostAuth();
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "stretch" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input::placeholder { color: #374151; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div style={{
        flex: 1, background: BG,
        display: "flex", flexDirection: "column", animation: "fadeIn 0.4s ease", minHeight: "100vh",
      }}>
        {screen === "login" && (
          <LoginScreen
            onNavigate={handleNavigate}
            onLogin={handleLogin}
            onGoogleLogin={loginWithGoogle}
            error={authError}
          />
        )}
        {screen === "forgot" && (
          <ForgotPasswordScreen onNavigate={handleNavigate} />
        )}
        {screen === "signup" && (
          <SignupScreen
            onNavigate={handleNavigate}
            onSignup={handleSignup}
            onGoogleLogin={loginWithGoogle}
          />
        )}
      </div>
    </div>
  );
}
